import React from 'react';
import { format, parseISO } from 'date-fns';

import { Box } from '@zoopla-eng/zds-components/box';
import { HelpSmall } from '@zoopla-eng/zds-components/icons';
import { Badge } from '@zoopla-eng/zds-components/badges';
import { Button } from '@zoopla-eng/zds-components/button';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { useOptimizelyFeatureFlag } from '@zoopla/optimizely';

import ValueChangeText from '../ValueChange/ValueChange';

import { Maybe, ValueChange, ConfidenceLevel } from '../../graphql.generated';

import Timestamp from '../Timestamp/Timestamp';
import ConsentAction from '../ConsentAction/ConsentAction';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import { sinceLastSoldStyle } from '../Estimate/Estimate.styles.css';
import {
    estimateRangeStyle,
    timestampWrapperStyle,
    priceStyle,
} from './EstimateDetails.styles.css';

export type EstimateDetailsProps = {
    currentPrice: string;
    estimateRange: string;
    estimateLabel: string;
    rangeLabel: string;
    confidenceLevel?: ConfidenceLevel;
    valueChange?: Maybe<ValueChange>;
    ingestedAt?: Maybe<string>;
    onEstimateExplainerClick: () => void;
    uprn: string;
};

const EstimateDetails: React.FC<
    React.PropsWithChildren<EstimateDetailsProps>
> = ({
    currentPrice,
    estimateRange,
    estimateLabel,
    rangeLabel,
    confidenceLevel,
    valueChange,
    ingestedAt,
    onEstimateExplainerClick,
    uprn,
}) => {
    const [displayCountdown] = useOptimizelyFeatureFlag(
        'hom_countdown_timestamp'
    );
    const lastSoldDate = valueChange?.saleDate
        ? format(parseISO(valueChange.saleDate), 'MMMM yyyy')
        : '';

    return (
        <>
            <Button category="tertiary" onClick={onEstimateExplainerClick}>
                <Stack flexDirection="row" alignItems="center" columnGap="S200">
                    <span>{estimateLabel}</span>
                    <HelpSmall />
                </Stack>
            </Button>

            <h2 className={`${priceStyle} ${fontGroups.teapot}`}>
                {currentPrice}
            </h2>

            {confidenceLevel && (
                <Badge level={4} text={`${confidenceLevel} confidence`} />
            )}

            <Box pt="S400">
                <p className={fontGroups.ladle}>{rangeLabel}</p>
                <p className={`${estimateRangeStyle} ${fontGroups.mug}`}>
                    {estimateRange}
                </p>
            </Box>
            <Box
                display="flex"
                alignItems="center"
                columnGap="S400"
                pt="S400"
                pb="S200"
            >
                <div className={timestampWrapperStyle}>
                    {ingestedAt && (
                        <Timestamp
                            ingestedAt={ingestedAt}
                            displayCountdown={displayCountdown}
                            displayIcon={!displayCountdown}
                        />
                    )}
                </div>

                <ConsentAction uprn={uprn} />
            </Box>

            {valueChange && (
                <Box pt="S400">
                    <ValueChangeText
                        numericChange={valueChange.numericChange}
                        percentageChange={valueChange.percentageChange}
                    />
                    <div
                        className={sinceLastSoldStyle}
                    >{`Since last sold - ${lastSoldDate}`}</div>
                </Box>
            )}
        </>
    );
};

export default EstimateDetails;
